import {
  ETYKIETY_JAKOSCI,
  KOLEJNOSC_JAKOSCI,
  OPISY_JAKOSCI,
  WARIANTY_JAKOSCI,
  type JakoscWypowiedzi,
} from "../../lib/jakoscWypowiedzi.js";
import { Badge } from "../ui/Badge.js";
import { Checkbox } from "../ui/Choice.js";

/**
 * Legenda kolorow wypowiedzi w transkrypcie: wspolna dla widoku administratora, wyniku uzytkownika i linku recenzenta.
 *
 * Kolor wynika wylacznie ze zweryfikowanych wskazan oceny (shared/koloryWypowiedzi.ts) i NIE jest skala
 * ocen: wypowiedz bez zweryfikowanych wskazan zostaje bez koloru. Decyzja autora metodyki (29.10.2026): najlepsze sa
 * zlote, najslabsze szare, a srednie zostaja bez oznaczenia, wiec dymek sredni wyglada jak dymek bez wskazan, a probka
 * "srednie" jak probka "bez koloru". Odroznia je tylko plakietka przy dymku, dlatego nazwa jakosci stoi w legendzie
 * w tej samej plakietce co przy dymku (zlota i szara z kropka, neutralna "srednie"), a pozycja "bez koloru" ma zwykly
 * tekst, bo taka wypowiedz plakietki nie ma. Mowi o tym tez zdanie nad lista. Kazda pozycja ma slowa, a nie sam kolor (A-7).
 *
 * Pod kolorami dymkow stoi opis zaznaczen cytatow, z probkami w tych samych klasach co w tekscie (sama paleta nie
 * wystarczy): mocna strona jest podswietlona na zloto, a to, co do wymiany, jest szare i podkreslone. Rodzaj wskazania
 * niesie tez KSZTALT zaznaczenia (tlo albo linia), nie sam kolor. W wyniku uzytkownika przy dymkach nie ma wskazan,
 * a u administratora przelacznik je chowa, wiec bez tego zdania widzacy nie mialby objasnienia zaznaczen.
 */

function Probka({ jakosc }: { jakosc: JakoscWypowiedzi | "brak" }): React.JSX.Element {
  return <span className="legenda-jakosci__probka" data-jakosc={jakosc} aria-hidden="true" />;
}

export function LegendaJakosci({
  przelacznikUwag,
}: {
  /** Przelacznik wskazan przy dymkach. Brak: nie ma ich do pokazania, wiec nie ma czym przelaczac. */
  przelacznikUwag?: { pokazane: boolean; onZmiana: (pokazane: boolean) => void };
}): React.JSX.Element {
  return (
    <div className="legenda-jakosci" role="group" aria-label="Legenda kolorów wypowiedzi">
      <p className="legenda-jakosci__lead">
        Kolor wypowiedzi wynika wyłącznie ze zweryfikowanych wskazań oceny wg metodyki. Średnie zostają bez koloru, z samą
        plakietką „średnie”, a wypowiedź bez koloru i bez plakietki nie ma zweryfikowanych wskazań.
      </p>
      <ul className="legenda-jakosci__lista">
        {KOLEJNOSC_JAKOSCI.map((jakosc) => (
          <li className="legenda-jakosci__pozycja" key={jakosc}>
            <Probka jakosc={jakosc} />
            <span>
              <Badge wariant={WARIANTY_JAKOSCI[jakosc]} kropka>
                {ETYKIETY_JAKOSCI[jakosc]}
              </Badge>
              {`: ${OPISY_JAKOSCI[jakosc]}`}
            </span>
          </li>
        ))}
        <li className="legenda-jakosci__pozycja">
          <Probka jakosc="brak" />
          <span>
            <strong className="legenda-jakosci__nazwa">bez koloru</strong>
            {": brak zweryfikowanych wskazań przy tej wypowiedzi"}
          </span>
        </li>
      </ul>
      <p className="legenda-jakosci__cytaty">
        Cytaty w tekście:{" "}
        <span className="cytat-podswietlony" data-rodzaj="mocna-strona">
          podświetlony na złoto
        </span>{" "}
        to cytat z mocnej strony,{" "}
        <span className="cytat-podswietlony" data-rodzaj="do-poprawy">
          szary i podkreślony
        </span>{" "}
        to cytat ze wskazania do poprawy. Cytatu, którego nie znaleźliśmy w wypowiedzi słowo w słowo, nie zaznaczamy.
      </p>
      {przelacznikUwag && (
        <div className="legenda-jakosci__przelacznik">
          <Checkbox
            etykieta="Pokaż wskazania przy wypowiedziach"
            zaznaczony={przelacznikUwag.pokazane}
            onZmiana={przelacznikUwag.onZmiana}
          />
        </div>
      )}
    </div>
  );
}
